/**
 * InstructionSignModal – Unterschrift-Pad für Unterweisungen
 * Verwendung:
 *   - Mitarbeiter: "Gelesen & Unterschreiben" → POST /instructions/{id}/confirm mit signature_ma
 *   - Admin/SL: beim Versenden oder rückwirkend → PUT /instructions/{id}/sign-sl
 *   - Rückwirkend MA: PUT /instructions/{id}/mitarbeiter/{empId}/sign-ma
 */
import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import SignaturePad from './ui/SignaturePad';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Loader2, RotateCcw, PenLine } from 'lucide-react';

export const InstructionSignModal = ({
  open,
  onClose,
  onSign,           // async (signatureBase64) => void
  title,            // Titel der Unterweisung
  signerLabel,      // "Mitarbeiter" oder "Stationsleitung / Admin"
  signerName,       // Name der unterzeichnenden Person
  confirmLabel,     // Text auf dem Bestätigen-Button
  isEmployee,       // true = MA-Flow (zeigt zusätzl. "Ich habe diese Unterweisung gelesen und verstanden")
}) => {
  const sigRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [empty, setEmpty] = useState(true);

  const handleClear = () => {
    sigRef.current?.clear();
    setEmpty(true);
  };

  const handleEnd = () => {
    setEmpty(sigRef.current?.isEmpty() ?? true);
  };

  const handleConfirm = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) return;
    setSaving(true);
    try {
      const sig = sigRef.current.toDataURL('image/png');
      await onSign(sig);
      onClose();
    } catch (e) {
      // Fehlerbehandlung im Parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="instruction-sign-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="h-5 w-5 text-blue-600" />
            Unterschrift – {signerLabel}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {title && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg px-3 py-2 text-sm text-blue-800 dark:text-blue-200">
              <span className="font-medium">Unterweisung:</span> {title}
            </div>
          )}

          {signerName && (
            <p className="text-sm text-muted-foreground">
              Unterzeichner: <span className="font-semibold text-foreground">{signerName}</span>
            </p>
          )}

          {isEmployee && (
            <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
              Mit Ihrer Unterschrift bestätigen Sie, dass Sie diese Unterweisung gelesen und verstanden haben.
            </p>
          )}

          {/* Unterschrift-Feld */}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Bitte hier unterschreiben:</p>
            <div
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl overflow-hidden bg-white"
              style={{ touchAction: 'none' }}
            >
              <SignaturePad ref={sigRef} penColor="#1e3a8a" height={180} onEnd={handleEnd} />
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="mt-1.5 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
            >
              <RotateCcw className="h-3 w-3" />
              Löschen
            </button>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={empty || saving}
            data-testid="instruction-sign-confirm-btn"
            className="gap-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel || 'Unterschreiben & Bestätigen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InstructionSignModal;
